import {
  Tooltip as AntTooltip,
  Dropdown as AntDropdown,
  Form as AntForm,
  Checkbox as AntCheckbox,
  Radio as AntRadio,
} from "antd";
import { InfoCircleOutlined } from "@ant-design/icons";
import {
  REBRAND_PRIMARY_COLOR,
  REBRAND_SECONDARY_COLOR,
} from "@src/appV2/lib/Theme/rebrandConstants";
import { WorkerName, WorkerRow } from "src/containers/workers/ListWorkers/ListWorkers.styles";
import styled, { css } from "styled-components";

const activeRow = css`
  color: #032e68;
  cursor: pointer;
  margin-bottom: 0;

  &:hover {
    text-decoration: underline;
  }
`;

export const Row = styled(WorkerRow)<{ isLoggedInUser?: boolean }>`
  cursor: auto;

  &:hover {
    ${WorkerName} {
      text-decoration: none;
    }
  }

  ${WorkerName} {
    a {
      ${({ isLoggedInUser }) => (isLoggedInUser ? activeRow : "")};
    }
  }

  [data-rebrand] & ${WorkerName} a {
    color: ${REBRAND_SECONDARY_COLOR};
  }
`;

export const Tooltip = styled(AntTooltip)`
  .ant-tooltip-arrow-content:before,
  .ant-tooltip-inner {
    background: white;
    box-shadow: 0px 4px 4px rgba(66, 66, 66, 0.25);
    color: #333;
  }
  .ant-tooltip-inner {
    border-radius: 6px;
    padding: 8px;
  }

  a {
    color: #032e68;

    &:hover {
      color: #032e68;
      border-bottom-color: #032e68;
    }
  }

  [data-rebrand] & a {
    color: ${REBRAND_SECONDARY_COLOR};

    &:hover {
      color: ${REBRAND_SECONDARY_COLOR};
      border-bottom-color: ${REBRAND_SECONDARY_COLOR};
    }
  }
`;

export const Dropdown = styled(AntDropdown)`
  width: 260px;
  display: flex;
  justify-content: space-between;
  background-color: white;
  border: 1px solid #9ca3af;
  border-radius: 8px;
  line-height: 23px;
  align-items: center;
  padding: 4px 11px;

  &:disabled {
    background: #f5f5f5;
    cursor: not-allowed;
    border-color: #d9d9d9;
    color: rgba(0, 0, 0, 0.25);
  }

  &:hover {
    border-color: #032e68;
  }

  &:focus {
    border-color: #032e68;
    box-shadow: 0 0 0 2px rgba(3, 46, 104, 14%);
  }

  [data-rebrand] &:hover {
    border-color: ${REBRAND_PRIMARY_COLOR};
  }

  [data-rebrand] &:focus {
    border-color: ${REBRAND_PRIMARY_COLOR};
    box-shadow: 0 0 0 2px rgba(218, 36, 81, 14%);
  }
`;

export const DropdownOverlay = styled.div`
  width: 260px;
  padding: 6px 12px;
  border-radius: 8px;
  background-color: white;
  border: 1px solid #9ca3af;

  .ant-radio-wrapper {
    height: 32px;
    align-items: center;
  }

  .ant-radio {
    top: unset;
  }

  .ant-checkbox-group {
    width: 100%;
    padding-left: 0.75rem;
  }

  .ant-checkbox-wrapper {
    height: 32px;
    width: 100%;
    align-items: center;
  }

  .ant-checkbox {
    & + span {
      padding-right: 0;
    }
  }
`;

export const Form = styled(AntForm)`
  display: flex;
  justify-content: space-between;
  flex-wrap: wrap;
` as typeof AntForm;

export const FormItem = styled(AntForm.Item)`
  flex-direction: column;
  width: 260px;
  margin-bottom: 8px;

  .ant-form-item-label {
    text-align: left;
    line-height: 28px;
  }

  .ant-form-item-control {
    min-height: unset;
  }

  input {
    border-radius: 8px;
    border-color: #9ca3af;

    &:hover {
      border-color: #032e68;
    }

    &:focus {
      box-shadow: 0 0 0 2px rgba(3, 46, 104, 14%);
      border-color: #032e68;
    }
  }

  [data-rebrand] & input:hover {
    border-color: ${REBRAND_PRIMARY_COLOR};
  }

  [data-rebrand] & input:focus {
    border-color: ${REBRAND_PRIMARY_COLOR};
    box-shadow: 0 0 0 2px rgba(218, 36, 81, 14%);
  }
`;

export const NumberPrefix = styled.span`
  width: 16px;
  display: inline-block;
`;

export const CheckboxGroup = styled(AntCheckbox.Group)`
  .ant-checkbox-wrapper:hover .ant-checkbox-inner {
    border-color: #032e68;
  }
  .ant-checkbox {
    top: unset;

    &:after {
      border-color: #032e68;
    }
  }
  .ant-checkbox-checked {
    .ant-checkbox-inner {
      background: #032e68;
      border-color: #032e68;
    }
  }

  [data-rebrand] & .ant-checkbox-wrapper:hover .ant-checkbox-inner {
    border-color: ${REBRAND_PRIMARY_COLOR};
  }
  [data-rebrand] & .ant-checkbox:after {
    border-color: ${REBRAND_PRIMARY_COLOR};
  }
  [data-rebrand] & .ant-checkbox-checked .ant-checkbox-inner {
    background: ${REBRAND_PRIMARY_COLOR};
    border-color: ${REBRAND_PRIMARY_COLOR};
  }
`;

export const Radio = styled(AntRadio)`
  .ant-radio:hover .ant-radio-inner {
    border-color: #032e68;
  }

  .ant-radio-input:focus + .ant-radio-inner {
    box-shadow: 0 0 0 3px rgba(3, 46, 104, 14%);
  }

  .ant-radio-checked .ant-radio-inner {
    border: 2px solid #032e68;
    &:after {
      background: #032e68;
    }
  }

  [data-rebrand] & .ant-radio:hover .ant-radio-inner {
    border-color: ${REBRAND_PRIMARY_COLOR};
  }

  [data-rebrand] & .ant-radio-input:focus + .ant-radio-inner {
    box-shadow: 0 0 0 3px rgba(218, 36, 81, 14%);
  }

  [data-rebrand] & .ant-radio-checked .ant-radio-inner {
    border-color: ${REBRAND_PRIMARY_COLOR};
    &:after {
      background: ${REBRAND_PRIMARY_COLOR};
    }
  }
`;

export const BulkActionsContainer = styled.div`
  display: flex;
  gap: 12px;
  margin-bottom: 16px;
  margin-top: 16px;
  justify-content: flex-end;
`;

export const ColumnHeaderWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

export const SortableHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  cursor: pointer;
`;

function getFilterIconColor(isActive: boolean, isRebrand: boolean): string {
  if (!isActive) {
    return "#868686";
  }
  return isRebrand ? REBRAND_SECONDARY_COLOR : "#032e68";
}

export const FilterIcon = styled.span<{ $isActive: boolean; $isRebrand?: boolean }>`
  cursor: pointer;
  color: ${({ $isActive, $isRebrand }) => getFilterIconColor($isActive, $isRebrand ?? false)};
  font-size: 20px;
`;

export const FilterDropdownContent = styled.div`
  padding: 8px;
`;

export const EmailWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  overflow: visible;
`;

export const EmailText = styled.span`
  word-break: break-word;
  overflow: visible;
`;

export const InfoIcon = styled(InfoCircleOutlined)`
  color: #868686;
`;
